import { type FC, useEffect, useState } from 'react';
import { DatePicker, Modal, Textarea } from '@g2u/ui-kit';
import { Form } from 'antd';
import { format } from 'date-fns';

import { validateFormSilently } from 'helpers';

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (reason: string, endDate: string) => void;
  isLoading?: boolean;
};

type FormFields = {
  reason: string;
  endDate: Date;
};

export const CancelSubscriptionModal: FC<Props> = ({ isOpen, onCancel, onConfirm, isLoading }) => {
  const [form] = Form.useForm<FormFields>();
  const [isFormValid, setIsFormValid] = useState(false);

  // Modal.$ does set destroyOnClose, which resets the form's own field values on reopen, but
  // isFormValid lives in this component, not inside the Modal's content — it survives the
  // Modal's remount untouched. Without resetting it here too, Confirm can stay enabled after a
  // reopen even though the reason field is back to empty.
  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({ endDate: new Date(), reason: '' });
      setIsFormValid(false);
    }
  }, [isOpen, form]);

  const handleFormChange = () => {
    validateFormSilently(form, setIsFormValid);
  };

  const handleFinish = (values: FormFields) => {
    onConfirm(values.reason, format(values.endDate, 'yyyy-MM-dd'));
  };

  return (
    <Modal.$
      open={isOpen}
      title="Cancel Subscription"
      okText="Confirm"
      cancelText="Cancel"
      onOk={() => form.submit()}
      onCancel={onCancel}
      okButtonProps={{ disabled: !isFormValid, loading: isLoading }}
      dataTestId="cancel-subscription-modal"
    >
      <Form form={form} layout="vertical" preserve={false} onValuesChange={handleFormChange} onFinish={handleFinish}>
        <DatePicker.$
          label="Cancellation Date"
          name="endDate"
          required
          rules={[{ required: true, message: 'Please select a cancellation date.' }]}
          allowClear={false}
          initialValue={new Date() as any}
        />
        <Textarea.$
          label="Cancellation Reason"
          name="reason"
          required
          rules={[{ required: true, message: 'Please enter a cancellation reason.' }]}
        />
      </Form>
    </Modal.$>
  );
};
